/**
 * A single aerodynamic panel — one strip of wing, tailplane, fin or fuselage.
 *
 * ## Why panels rather than whole-aircraft coefficients
 *
 * A conventional stability-derivative model (`Cl_p`, `Cn_beta`, `Cm_q`, …) is a linearisation
 * about cruise. It flies beautifully until the aeroplane stops behaving linearly, which is
 * precisely where a flight simulator earns its keep: the stall, the wing drop, the spin, the
 * tumble. Those all come from *one wing panel doing something different from another*.
 *
 * So the aircraft is built from panels, each of which independently:
 *
 *   - samples the airflow at its own location, including the contribution of body rotation
 *     `ω × r` — this is where roll damping, pitch damping and yaw damping come from, for free
 *     and with the right magnitudes, instead of being tuned coefficients;
 *   - computes its own local angle of attack and its own coefficients from a −180…180° table;
 *   - produces a force at its own position, so the moment about the centre of gravity is
 *     `r × F` rather than a separate moment model.
 *
 * The emergent consequences are the whole point:
 *
 *   - **Wing drop.** In a stall with any roll rate or sideslip, one panel is past its stall
 *     angle while the other is not. The lift asymmetry rolls the aeroplane, which increases
 *     the asymmetry. Nobody scripted it.
 *   - **Spin.** The dropping wing is deeply stalled (low lift, high drag) while the rising
 *     wing is not; the drag asymmetry yaws the aircraft into the drop and the autorotation
 *     sustains itself. Recovery works because opposite rudder and forward stick genuinely
 *     unstall the inner panel.
 *   - **Adverse yaw.** The down-going aileron adds lift *and* induced drag on its side.
 *   - **Dihedral effect.** Sideslip changes each panel's local alpha through the dihedral
 *     angle, rolling the aircraft away from the slip.
 *
 * ## Control surfaces shift alpha, they do not add lift
 *
 * A deflected control surface is modelled as an *effective angle-of-attack shift*,
 * `α_eff = α + τ·δ`, and the shifted alpha is looked up in the same full-circle table. This
 * matters enormously: on a stalled panel, the table is past its peak, so a deflected aileron
 * produces almost nothing — which is exactly why picking up a dropping wing with aileron
 * makes a stall worse, and why the rudder is the right control. A `ΔCL = k·δ` model gets that
 * backwards and lets the pilot aileron their way out of a spin.
 *
 * ## Allocation
 *
 * `accumulate()` runs on the 120 Hz tick and allocates nothing: all working vectors are
 * module-level scratch.
 */

import { Vector3 } from 'three';
import { AeroTable, makeAeroCoefficients } from './tables.js';
import { DEG2RAD, clamp } from '../../core/math.js';

/** Which axis a panel's "lift" acts along. */
export type PanelKind = 'horizontal' | 'vertical';

/** How each control input drives this panel, as an effective alpha shift per unit input. */
export interface PanelControlGains {
  /**
   * Elevator effectiveness τ [rad of effective alpha per rad of surface deflection].
   * Typical for a plain flap-type surface with 25 % chord ratio: 0.5.
   */
  elevator?: number;
  /** Aileron effectiveness. Sign is baked in per panel, so the left panel carries −τ. */
  aileron?: number;
  /** Rudder effectiveness. */
  rudder?: number;
}

export interface PanelConfig {
  name: string;
  /** Aerodynamic centre relative to the centre of gravity, body axes [m]. */
  positionM: readonly [number, number, number];
  /** Reference area [m²]. */
  areaM2: number;
  /** Mean chord [m], used for the pitching moment. */
  chordM: number;
  /** Span of this panel [m], used for ground effect. */
  spanM: number;
  kind: PanelKind;
  /** Rigging incidence relative to the body x axis [deg], nose-up positive. */
  incidenceDeg?: number;
  /** Dihedral [deg], tip-up positive. Only meaningful for horizontal panels. */
  dihedralDeg?: number;
  /** +1 for a right-hand panel, −1 for left, 0 for a centreline panel. Sets the dihedral sign. */
  side?: number;
  /** Clean-configuration coefficients. */
  table: AeroTable;
  /** Fully-deployed-flap coefficients, blended in by flap fraction. */
  flappedTable?: AeroTable;
  /** Extra drag coefficient at full flap, on top of the flapped table. */
  flapDragDelta?: number;
  /** Extra pitching-moment coefficient at full flap. */
  flapMomentDelta?: number;
  controls?: PanelControlGains;
  /** Fraction of this panel washed by the propeller slipstream, 0…1. */
  slipstreamFraction?: number;
  /** Does this panel feel ground effect? True for the wing, false for the fin. */
  groundEffect?: boolean;
  /** Height of the panel above the wheels at rest [m], for the ground-effect height. */
  heightAboveGearM?: number;
  /**
   * How much of the wing's downwash this panel flies in, 0…1. The tailplane sits squarely in
   * it (≈1); a fin barely notices it, because downwash is a vertical deflection and a vertical
   * surface measures horizontal ones.
   */
  downwashFactor?: number;
  /** Does this panel's lift *generate* downwash? True for the wing panels. */
  contributesDownwash?: boolean;
}

/** Everything a panel needs to know about the current flight condition. */
export interface FlowContext {
  /** Air density [kg/m³]. */
  densityKgPerM3: number;
  /** Aircraft velocity relative to the air mass, body axes [m/s]. */
  velocityBody: Vector3;
  /** Body rates `(p, q, r)` [rad/s]. */
  angularVelocityBody: Vector3;
  /** Elevator deflection [rad], trailing edge down positive (which pitches the nose down). */
  elevatorRad: number;
  /** Aileron deflection [rad], positive rolls right. */
  aileronRad: number;
  /** Rudder deflection [rad], positive yaws the nose right. */
  rudderRad: number;
  /** Flap deployment, 0…1. */
  flapFraction: number;
  /** Height of the centre of gravity above the ground [m]. */
  heightAboveGroundM: number;
  /** Axial velocity increment inside the propeller slipstream [m/s]. */
  slipstreamAxialMps: number;
  /** Lateral (swirl) velocity inside the slipstream [m/s], body y. */
  slipstreamSwirlYMps: number;
  /** Vertical (swirl) velocity inside the slipstream [m/s], body z. */
  slipstreamSwirlZMps: number;
  /**
   * Downward air velocity behind the wing [m/s], from the wing's own lift.
   *
   * This is the single most important interaction between the wing and the tail. Without it
   * the tailplane sees the full free-stream angle of attack, cannot generate the download that
   * balances a nose-heavy aeroplane, and the aircraft runs out of elevator long before it
   * reaches maximum lift — a stall speed far too high, for reasons that look nothing like the
   * actual cause. It is also why lowering flaps pitches the nose down: more lift, more
   * downwash, less tail angle of attack.
   */
  downwashMps: number;
}

export function makeFlowContext(): FlowContext {
  return {
    densityKgPerM3: 1.225,
    velocityBody: new Vector3(),
    angularVelocityBody: new Vector3(),
    elevatorRad: 0,
    aileronRad: 0,
    rudderRad: 0,
    flapFraction: 0,
    heightAboveGroundM: 1000,
    slipstreamAxialMps: 0,
    slipstreamSwirlYMps: 0,
    slipstreamSwirlZMps: 0,
    downwashMps: 0,
  };
}

/** Diagnostics for one panel, for the debug HUD and the tests. */
export interface PanelDiagnostics {
  alphaRad: number;
  cl: number;
  cd: number;
  dynamicPressurePa: number;
  liftN: number;
  stalled: boolean;
}

const cleanCoefficients = /* #__PURE__ */ makeAeroCoefficients();
const flappedCoefficients = /* #__PURE__ */ makeAeroCoefficients();

export class AeroPanel {
  readonly name: string;
  readonly position = new Vector3();
  readonly areaM2: number;
  readonly chordM: number;
  readonly spanM: number;
  readonly kind: PanelKind;

  /** Lift-curve slope through attached flow [1/rad], measured from this panel's own table. */
  readonly liftSlopePerRad: number;

  /** Chord axis in body coordinates — the panel's "forward". */
  readonly chordAxis = new Vector3();
  /** Normal axis in body coordinates — the direction positive lift acts. */
  readonly normalAxis = new Vector3();

  private readonly table: AeroTable;
  private readonly flappedTable: AeroTable | undefined;
  private readonly flapDragDelta: number;
  private readonly flapMomentDelta: number;
  private readonly elevatorGain: number;
  private readonly aileronGain: number;
  /**
   * Rudder effectiveness τ and the fraction of this panel in the propwash.
   *
   * Public because they are *descriptions of the airframe*, not internal state, and because the
   * rudder-authority estimate the coordination assist inverts is built from exactly these two
   * numbers plus the area and the arm. Reading them off the panel is what keeps that estimate
   * data-driven rather than a second copy of the fin written down somewhere else.
   */
  readonly rudderGain: number;
  readonly slipstreamFraction: number;
  private readonly downwashFactor: number;
  /** True when this panel's lift feeds the downwash the tail flies in. */
  readonly contributesDownwash: boolean;
  /** True when this panel flies in the wing's downwash and must be evaluated after it. */
  readonly receivesDownwash: boolean;
  private readonly groundEffect: boolean;
  private readonly heightAboveGearM: number;
  /**
   * The angle at which this panel's lift peaks [rad].
   *
   * Public because two things outside the aerodynamics need the *real* number rather than a
   * plausible constant: the stall horn and pre-stall buffet in `audio/`, and the HUD's AoA
   * bracket. Both used to carry a hard-coded 17°, which was right for this wing and would have
   * been silently wrong for the next one.
   */
  readonly stallAlphaRad: number;

  /** Last-computed diagnostics. Read after `accumulate`; do not retain. */
  readonly diagnostics: PanelDiagnostics = {
    alphaRad: 0,
    cl: 0,
    cd: 0,
    dynamicPressurePa: 0,
    liftN: 0,
    stalled: false,
  };

  constructor(config: PanelConfig) {
    this.name = config.name;
    this.position.set(config.positionM[0], config.positionM[1], config.positionM[2]);
    this.areaM2 = config.areaM2;
    this.chordM = config.chordM;
    this.spanM = config.spanM;
    this.kind = config.kind;
    this.table = config.table;
    this.flappedTable = config.flappedTable;
    this.flapDragDelta = config.flapDragDelta ?? 0;
    this.flapMomentDelta = config.flapMomentDelta ?? 0;
    this.elevatorGain = config.controls?.elevator ?? 0;
    this.aileronGain = config.controls?.aileron ?? 0;
    this.rudderGain = config.controls?.rudder ?? 0;
    this.slipstreamFraction = clamp(config.slipstreamFraction ?? 0, 0, 1);
    this.liftSlopePerRad = config.table.liftSlopePerRad;
    this.downwashFactor = clamp(config.downwashFactor ?? 0, 0, 1);
    this.receivesDownwash = this.downwashFactor > 0;
    this.contributesDownwash = config.contributesDownwash ?? false;
    this.groundEffect = config.groundEffect ?? false;
    this.heightAboveGearM = config.heightAboveGearM ?? 0;
    this.stallAlphaRad = config.table.stallAlphaRad;

    buildPanelAxes(
      config.kind,
      (config.incidenceDeg ?? 0) * DEG2RAD,
      (config.dihedralDeg ?? 0) * DEG2RAD,
      config.side ?? 0,
      this.chordAxis,
      this.normalAxis,
    );
  }

  /**
   * Add this panel's force and moment (about the centre of gravity) to the accumulators.
   * Allocation-free.
   */
  accumulate(context: FlowContext, forceAccumulator: Vector3, momentAccumulator: Vector3): void {
    // --- local airflow --------------------------------------------------------------
    // Velocity of *this point* on the airframe relative to the air: the aircraft's velocity
    // plus the velocity due to rotation about the centre of gravity. The ω × r term is the
    // entire source of rate damping in this model.
    //
    // Written out in scalars rather than through Vector3 methods. Each of those is a call
    // returning a double, and when V8 declines to inline one the double is boxed into a
    // HeapNumber — with eight panels and a dozen calls each that measured 300 B of garbage
    // every tick, which is 36 KB/s at 120 Hz and a scavenge every few seconds.
    const velocity = context.velocityBody;
    const omega = context.angularVelocityBody;
    const rx = this.position.x;
    const ry = this.position.y;
    const rz = this.position.z;

    let flowX = velocity.x + (omega.y * rz - omega.z * ry);
    let flowY = velocity.y + (omega.z * rx - omega.x * rz);
    let flowZ = velocity.z + (omega.x * ry - omega.y * rx);

    // The propeller slipstream adds axial velocity and swirl to whatever it washes over.
    const slipstream = this.slipstreamFraction;
    if (slipstream > 0) {
      flowX += context.slipstreamAxialMps * slipstream;
      flowY += context.slipstreamSwirlYMps * slipstream;
      flowZ += context.slipstreamSwirlZMps * slipstream;
    }

    // Downwash: the air behind the wing is moving down, so this panel's velocity relative to
    // it has less downward component — a reduced angle of attack.
    if (this.downwashFactor > 0) {
      flowZ -= context.downwashMps * this.downwashFactor;
    }

    // Project into the panel's own plane. Spanwise flow is ignored, as in strip theory.
    const chordX = this.chordAxis.x;
    const chordY = this.chordAxis.y;
    const chordZ = this.chordAxis.z;
    const normalX = this.normalAxis.x;
    const normalY = this.normalAxis.y;
    const normalZ = this.normalAxis.z;

    const along = flowX * chordX + flowY * chordY + flowZ * chordZ;
    const normalComponent = -(flowX * normalX + flowY * normalY + flowZ * normalZ);
    const speedSquared = along * along + normalComponent * normalComponent;

    if (speedSquared < 1e-8) {
      const idle = this.diagnostics;
      idle.alphaRad = 0;
      idle.cl = 0;
      idle.cd = 0;
      idle.dynamicPressurePa = 0;
      idle.liftN = 0;
      idle.stalled = false;
      return;
    }

    const speed = Math.sqrt(speedSquared);
    const alphaRad = Math.atan2(normalComponent, along);

    // --- control deflection as an effective alpha shift -------------------------------
    const deflection =
      this.elevatorGain * context.elevatorRad +
      this.aileronGain * context.aileronRad +
      this.rudderGain * context.rudderRad;
    const effectiveAlpha = alphaRad + deflection;

    // --- coefficients ----------------------------------------------------------------
    this.table.sample(effectiveAlpha, cleanCoefficients);
    let cl = cleanCoefficients.cl;
    let cd = cleanCoefficients.cd;
    let cm = cleanCoefficients.cm;

    const flap = context.flapFraction;
    if (this.flappedTable !== undefined && flap > 0) {
      this.flappedTable.sample(effectiveAlpha, flappedCoefficients);
      cl += (flappedCoefficients.cl - cl) * flap;
      cd += (flappedCoefficients.cd - cd) * flap;
      cm += (flappedCoefficients.cm - cm) * flap;
      cd += this.flapDragDelta * flap;
      cm += this.flapMomentDelta * flap;
    }

    // --- ground effect ----------------------------------------------------------------
    if (this.groundEffect && this.spanM > 0) {
      // Wieselsberger: induced drag falls as the ground blocks the trailing vortices. The
      // height that matters is the *wing's* height, not the CG's.
      const height = Math.max(0.05, context.heightAboveGroundM + this.heightAboveGearM);
      const ratio = (16 * height) / this.spanM;
      const ratioSquared = ratio * ratio;
      const inducedFactor = ratioSquared / (1 + ratioSquared);
      // Split drag into the part that scales with lift and the part that does not. Only the
      // induced part is relieved.
      const inducedDrag = Math.max(0, cd - 0.02);
      cd -= inducedDrag * (1 - inducedFactor);
      // …and a modest lift bonus, which is the "float" on landing.
      cl *= 1 + 0.08 * (1 - inducedFactor);
    }

    // --- forces ------------------------------------------------------------------------
    const dynamicPressure = 0.5 * context.densityKgPerM3 * speedSquared;
    const referenceForce = dynamicPressure * this.areaM2;
    const liftMagnitude = referenceForce * cl;
    const dragMagnitude = referenceForce * cd;

    // In the (chord, normal) plane: drag opposes the flow, lift is perpendicular to it.
    const inverseSpeed = 1 / speed;
    const dragAlong = -along * inverseSpeed;
    const dragNormal = normalComponent * inverseSpeed;
    const liftAlong = normalComponent * inverseSpeed;
    const liftNormal = along * inverseSpeed;

    const componentAlong = liftMagnitude * liftAlong + dragMagnitude * dragAlong;
    const componentNormal = liftMagnitude * liftNormal + dragMagnitude * dragNormal;

    const forceX = chordX * componentAlong + normalX * componentNormal;
    const forceY = chordY * componentAlong + normalY * componentNormal;
    const forceZ = chordZ * componentAlong + normalZ * componentNormal;

    forceAccumulator.x += forceX;
    forceAccumulator.y += forceY;
    forceAccumulator.z += forceZ;

    // Moment about the CG: the panel's own pitching moment plus the r × F of its force. The
    // panel's moment acts about its span axis, which is chord × normal.
    const pitchingMoment = referenceForce * this.chordM * cm;
    momentAccumulator.x += ry * forceZ - rz * forceY + pitchingMoment * (chordY * normalZ - chordZ * normalY);
    momentAccumulator.y += rz * forceX - rx * forceZ + pitchingMoment * (chordZ * normalX - chordX * normalZ);
    momentAccumulator.z += rx * forceY - ry * forceX + pitchingMoment * (chordX * normalY - chordY * normalX);

    // --- diagnostics -------------------------------------------------------------------
    this.diagnostics.alphaRad = alphaRad;
    this.diagnostics.cl = cl;
    this.diagnostics.cd = cd;
    this.diagnostics.dynamicPressurePa = dynamicPressure;
    this.diagnostics.liftN = liftMagnitude;
    this.diagnostics.stalled = Math.abs(effectiveAlpha) > Math.abs(this.stallAlphaRad);
  }
}

/**
 * Build a panel's chord and normal axes in body coordinates.
 *
 * Horizontal panels start with the chord forward (+x) and the normal up (−z), then rotate by
 * incidence about the span axis and by dihedral about the chord axis.
 *
 * The dihedral sign is the subtle one. A right wing with positive dihedral rises outboard, so
 * its surface normal tilts **inboard** — toward −y, not +y. Getting that backwards reverses
 * the dihedral effect: the aeroplane would roll *into* a sideslip instead of out of it, and it
 * would be spirally unstable in a way that is easy to mistake for "a bit twitchy".
 */
export function buildPanelAxes(
  kind: PanelKind,
  incidenceRad: number,
  dihedralRad: number,
  side: number,
  chordOut: Vector3,
  normalOut: Vector3,
): void {
  if (kind === 'vertical') {
    // Fin: chord forward, "lift" acts to starboard. Incidence rotates about the z axis.
    const cosI = Math.cos(incidenceRad);
    const sinI = Math.sin(incidenceRad);
    chordOut.set(cosI, sinI, 0);
    normalOut.set(-sinI, cosI, 0);
    return;
  }

  // Incidence: rotate about +y. Chord tilts nose-up, so the normal tilts aft.
  const cosI = Math.cos(incidenceRad);
  const sinI = Math.sin(incidenceRad);
  chordOut.set(cosI, 0, -sinI);
  normalOut.set(-sinI, 0, -cosI);

  // Dihedral: rotate about the chord axis by −side·Γ, which tilts the normal inboard.
  const roll = -Math.sign(side || 1) * dihedralRad;
  if (roll !== 0) {
    rotateAboutX(normalOut, roll);
    rotateAboutX(chordOut, roll);
  }
}

/** Rotate a vector about the body x axis. */
function rotateAboutX(vector: Vector3, angleRad: number): void {
  const cos = Math.cos(angleRad);
  const sin = Math.sin(angleRad);
  const y = vector.y * cos - vector.z * sin;
  const z = vector.y * sin + vector.z * cos;
  vector.y = y;
  vector.z = z;
}
